
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface HandoverTokenData {
  token: string;
  expiresAt: string;
  profileId: string;
}

interface ClaimProfileResult {
  profile_id: string;
  username: string;
  first_name: string;
  last_name: string;
  claimed_at: string;
}

export const useHandoverToken = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  const generateHandoverToken = async (profileId: string, expiresInHours: number = 168): Promise<string> => {
    if (!user?.id) throw new Error('User not authenticated');
    
    setIsGenerating(true);
    
    try {
      const { data, error } = await supabase
        .rpc('generate_handover_token', {
          profile_id_param: profileId,
          creator_id: user.id,
          expires_in_hours: expiresInHours
        });

      if (error) {
        console.error('Error generating handover token:', error);
        throw error;
      }

      toast({
        title: 'Handover token generated',
        description: 'Share this token with the person who should take over this profile.'
      });

      return data;
    } catch (error: any) {
      console.error('Error generating token:', error);
      toast({
        title: 'Error generating token',
        description: error.message || 'Failed to generate handover token.',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const claimProfileWithToken = async (tokenCode: string) => {
    if (!user?.id) throw new Error('User not authenticated');
    
    setIsClaiming(true);
    
    try {
      const { data, error } = await supabase
        .rpc('claim_profile_with_token', {
          token_code_param: tokenCode.toUpperCase(),
          new_owner_id: user.id
        });

      if (error) {
        console.error('Error claiming profile:', error);
        throw error;
      }

      // Type assertion with intermediate unknown conversion
      const profileData = data as unknown as ClaimProfileResult;

      toast({
        title: 'Profile claimed successfully',
        description: `You now own ${profileData.first_name || profileData.username}'s timeline!`
      });

      return profileData;
    } catch (error: any) {
      console.error('Error claiming profile:', error);
      toast({
        title: 'Error claiming profile',
        description: error.message || 'Failed to claim profile. Please check the token.',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setIsClaiming(false);
    }
  };

  const copyTokenToClipboard = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast({
        title: 'Token copied',
        description: 'Handover token copied to clipboard!'
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: 'Copy failed',
        description: 'Please copy the token manually.',
        variant: 'destructive'
      });
    }
  };

  return {
    generateHandoverToken,
    claimProfileWithToken,
    copyTokenToClipboard,
    isGenerating,
    isClaiming
  };
};
